import { detectFormat } from '../core/detect'
import { DetectionError } from '../core/errors'
import { type SourceFormat, type TargetFormat } from '../core/models'
import { convert } from '../core/pipeline'
import { bindFileInput } from './file-input'
import { LogPane } from './log'
import { StatusBar } from './status'
import { initTheme } from './theme'
import { InputPane, OutputPane } from './textarea'

function $<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id)
  if (!el) throw new Error(`#${id} not found`)
  return el as T
}

export function startApp(): void {
  const input = new InputPane($('input'), $('input-counter'))
  const output = new OutputPane(
    $('output'),
    $('output-counter'),
    $<HTMLButtonElement>('copy'),
    $<HTMLButtonElement>('download'),
  )
  const log = new LogPane($('log'))
  const status = new StatusBar($('status'))
  const detected = $('detected')
  const convertBtn = $<HTMLButtonElement>('convert')
  const formatRadios = document.querySelectorAll<HTMLInputElement>(
    'input[name="to"]',
  )

  initTheme($<HTMLButtonElement>('theme-toggle'))

  bindFileInput(
    $<HTMLButtonElement>('open-file'),
    $<HTMLInputElement>('file-input'),
    $('input'),
    (text) => input.setValue(text),
    (msg) => {
      log.error(msg)
      status.error(msg)
    },
  )

  let currentFormat: SourceFormat | null = null

  input.onChange((value) => {
    const trimmed = value.trim()
    if (!trimmed) {
      currentFormat = null
      detected.textContent = '—'
      detected.className = 'meta__detected-value meta__detected-value--neutral'
      input.setCount(0)
      convertBtn.disabled = true
      return
    }

    try {
      const fmt = detectFormat(value)
      currentFormat = fmt
      detected.textContent = fmt
      detected.className = 'meta__detected-value meta__detected-value--ok'
      convertBtn.disabled = false
    } catch (e) {
      currentFormat = null
      const msg = e instanceof DetectionError ? 'unknown' : (e as Error).message
      detected.textContent = msg
      detected.className = 'meta__detected-value meta__detected-value--err'
      convertBtn.disabled = true
    }

    input.setCount(estimateAccountCount(value))
  })

  convertBtn.addEventListener('click', () => {
    void runConvert()
  })

  async function runConvert(): Promise<void> {
    const value = input.getValue()
    if (!value.trim() || !currentFormat) return

    const toFormat = readSelectedFormat(formatRadios)
    log.clear()
    output.clear()
    status.converting()
    convertBtn.disabled = true

    try {
      const result = await convert({
        input: value,
        toFormat,
        fromFormat: currentFormat,
        onProgress: (m) => {
          if (m.startsWith('❌')) log.error(m)
          else if (m.startsWith('✅')) log.success(m)
          else log.info(m)
        },
      })
      output.setOutput(result.output, result.stats.success, `${toFormat}.txt`)
      status.done(result.stats)
    } catch (e) {
      const msg = (e as Error).message
      log.error(msg)
      status.error(msg)
    } finally {
      convertBtn.disabled = false
    }
  }
}

function readSelectedFormat(
  radios: NodeListOf<HTMLInputElement>,
): TargetFormat {
  for (const r of radios) {
    if (r.checked) return r.value as TargetFormat
  }
  return 'iam'
}

function estimateAccountCount(value: string): number {
  const trimmed = value.trim()
  if (!trimmed) return 0
  if (trimmed.includes('\n\n')) {
    return trimmed.split('\n\n').filter((b) => b.trim()).length
  }
  return trimmed.split('\n').filter((l) => l.trim()).length
}
